import {
  CalendarDays,
  CircleDollarSign,
  CreditCard,
  Info,
  Loader2,
  Search,
  Users,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Pagination from '../components/Pagination';
import { globalSettingsService, workerPayoutService } from '../services/api';
import './WorkerPayouts.css';

const PERIODS = [
  ['TODAY', 'Oggi'],
  ['WEEK', 'Settimana'],
  ['MONTH', 'Mese'],
  ['CUSTOM', 'Personalizzato'],
];

const STATUS_LABELS = {
  pending: 'Pending',
  in_transit: 'In transito',
  paid: 'Pagato',
  failed: 'Fallito',
  canceled: 'Annullato',
};

const SOURCE_LABELS = {
  WORKER: 'Worker',
  SAFETY_ADMIN: 'Safety Admin',
  BACKFILL: 'Storico Stripe',
};

const METHOD_LABELS = {
  INSTANT: 'Istantaneo',
  STANDARD: 'Standard',
};

function money(cents, currency = 'eur') {
  if (cents == null) return '—';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: String(currency).toUpperCase(),
  }).format(Number(cents) / 100);
}

function percentFromBps(bps) {
  return new Intl.NumberFormat('it-IT', {
    maximumFractionDigits: 2,
  }).format(Number(bps || 0) / 100);
}

function dateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function StatusBadge({ status }) {
  const normalized = String(status || 'pending').toLowerCase();
  return (
    <span className={`wp-status wp-status-${normalized}`}>
      {STATUS_LABELS[normalized] || normalized}
    </span>
  );
}

export default function WorkerPayouts() {
  const [period, setPeriod] = useState('MONTH');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');
  const [source, setSource] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (period === 'CUSTOM' && (!from || !to)) return;
    try {
      setLoading(true);
      setError('');
      const [response, settings] = await Promise.all([
        workerPayoutService.getPayouts({
          period,
          ...(period === 'CUSTOM' ? { from, to } : {}),
          tenantId,
          venueId,
          status,
          method,
          source,
          search,
          page,
          pageSize,
        }),
        globalSettingsService.getGlobalSettings(),
      ]);
      setData(response);
      setGlobalSettings(settings);
    } catch (loadError) {
      setError(loadError.response?.data?.error || loadError.message || 'Impossibile caricare i payout');
    } finally {
      setLoading(false);
    }
  }, [from, method, page, pageSize, period, search, source, status, tenantId, to, venueId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [period, from, to, tenantId, venueId, status, method, source, search]);

  const tenants = useMemo(() => data?.filterOptions?.tenants || [], [data]);
  const venues = useMemo(() => {
    if (!tenantId) return tenants.flatMap((tenant) => tenant.venues || []);
    return tenants.find((tenant) => tenant.id === tenantId)?.venues || [];
  }, [tenantId, tenants]);
  const summary = data?.summary || {};
  const pagination = data?.pagination || { total: 0, page: 1, pageSize, totalPages: 1 };

  const submitSearch = (event) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
  };

  return (
    <div className="wp-page">
      <header className="wp-header">
        <span className="wp-header-icon"><Wallet size={21} /></span>
        <div>
          <h1>Worker Payout</h1>
          <p>Storico dei payout Worker e Safety creati da SoonTip. Periodi e date sono calcolati in Europe/Rome.</p>
        </div>
      </header>

      <div className="wp-history-note">
        <Info size={16} aria-hidden="true" />
        <span>
          Lo storico disponibile parte dal 30/10/2026, data in cui è stata introdotta la possibilità di effettuare payout manuali istantanei.
        </span>
      </div>

      <div className="wp-periods" role="group" aria-label="Periodo statistiche">
        {PERIODS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={period === value ? 'active' : ''}
            onClick={() => setPeriod(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {period === 'CUSTOM' && (
        <div className="wp-custom-range">
          <label>Dal<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
          <label>Al<input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
        </div>
      )}

      <section className="wp-cards">
        <article className="wp-card wp-card-workers">
          <span>
            <Users size={18} /> Worker con payout
            <span
              className="wp-info"
              title="Ogni Worker viene conteggiato una sola volta, anche se associato a più locali."
              aria-label="Ogni Worker viene conteggiato una sola volta, anche se associato a più locali."
              tabIndex="0"
            >
              <Info size={13} />
            </span>
          </span>
          <strong>{Number(summary.workerWithPayoutPercent || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 })}%</strong>
          <small>{summary.workerWithPayoutCount || 0} su {summary.eligibleWorkerCount || 0} worker attivi e connessi</small>
          <small>
            {summary.connectedWorkerCount || 0} worker connessi totali · {summary.inactiveWorkerCount || 0} non attivi
          </small>
        </article>
        <article className="wp-card wp-card-count">
          <span><CalendarDays size={18} /> Payout effettuati</span>
          <strong>{summary.payoutCount || 0}</strong>
          <small>{data?.range ? `${data.range.from} – ${data.range.to}` : 'Periodo selezionato'}</small>
        </article>
        <article className="wp-card wp-card-volume">
          <span><CircleDollarSign size={18} /> Importo trasferito</span>
          <strong>{money(summary.payoutAmountCents || 0)}</strong>
          <small>Somma dei payout non falliti</small>
        </article>
        <article className="wp-card wp-card-fees">
          <span><CreditCard size={18} /> Commissioni</span>
          <strong>{money(summary.feeAmountCents || 0)}</strong>
          <small>
            Tariffa corrente: {percentFromBps(globalSettings?.workerInstantPayoutFeeBps)}% +{' '}
            {money(globalSettings?.workerInstantPayoutFeeFixedCents || 0)}
          </small>
        </article>
      </section>

      <section className="wp-filters">
        <label>
          Tenant
          <select value={tenantId} onChange={(event) => { setTenantId(event.target.value); setVenueId(''); }}>
            <option value="">Tutti i tenant</option>
            {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
          </select>
        </label>
        <label>
          Locale
          <select value={venueId} onChange={(event) => setVenueId(event.target.value)}>
            <option value="">Tutti i locali</option>
            {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
          </select>
        </label>
        <label>
          Stato
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Tutti</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          Metodo
          <select value={method} onChange={(event) => setMethod(event.target.value)}>
            <option value="">Tutti</option>
            {Object.entries(METHOD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          Origine
          <select value={source} onChange={(event) => setSource(event.target.value)}>
            <option value="">Tutte</option>
            {Object.entries(SOURCE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <form className="wp-search" onSubmit={submitSearch}>
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Worker, email, account, tenant o locale"
          />
          <button type="submit"><Search size={16} /> Cerca</button>
        </form>
      </section>

      {error && <div className="wp-error">{error}</div>}

      {loading ? (
        <div className="wp-loading"><Loader2 className="wp-spin" size={18} /> Caricamento payout...</div>
      ) : (
        <>
          <div className="wp-table-wrap">
            <table className="wp-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Worker</th>
                  <th>Tenant / Locale</th>
                  <th>Origine</th>
                  <th>Metodo</th>
                  <th>Lordo</th>
                  <th>Payout</th>
                  <th>Commissione</th>
                  <th>Residuo</th>
                  <th>Stato</th>
                  <th>Arrivo previsto</th>
                  <th>Riferimenti Stripe</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((row) => (
                  <tr key={row.id}>
                    <td><strong>{dateTime(row.stripeCreatedAt)}</strong></td>
                    <td>
                      <strong>{row.worker.name}</strong>
                      <small>{row.worker.email || '—'}</small>
                      <code>{row.worker.providerAccountId}</code>
                    </td>
                    <td>
                      {row.worker.affiliations.length > 0
                        ? row.worker.affiliations.map((affiliation) => (
                            <span className="wp-affiliation" key={affiliation.workerId}>
                              <strong>{affiliation.tenantName || '—'}</strong>
                              <small>{affiliation.venueName || '—'}</small>
                            </span>
                          ))
                        : '—'}
                    </td>
                    <td>
                      <span className={`wp-source wp-source-${String(row.source).toLowerCase()}`}>
                        {SOURCE_LABELS[row.source] || row.source}
                      </span>
                      {row.initiatedByEmail && <small>{row.initiatedByEmail}</small>}
                    </td>
                    <td>{METHOD_LABELS[row.method] || row.method}</td>
                    <td>{money(row.grossAmountCents, row.currency)}</td>
                    <td><strong className="wp-positive">{money(row.payoutAmountCents, row.currency)}</strong></td>
                    <td>{money(row.feeAmountCents, row.currency)}</td>
                    <td>{money(row.residualAmountCents, row.currency)}</td>
                    <td>
                      <StatusBadge status={row.status} />
                      {row.failureMessage && <small className="wp-failure">{row.failureMessage}</small>}
                    </td>
                    <td>{dateTime(row.arrivalAt)}</td>
                    <td>
                      <code>{row.stripePayoutId}</code>
                      {row.stripeBalanceTransactionId && <code>{row.stripeBalanceTransactionId}</code>}
                    </td>
                  </tr>
                ))}
                {(data?.rows || []).length === 0 && (
                  <tr><td className="wp-empty" colSpan="12">Nessun payout nel periodo selezionato.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination.total > 0 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={pagination.pageSize}
              onPageChange={setPage}
              onItemsPerPageChange={(value) => { setPageSize(value); setPage(1); }}
            />
          )}
        </>
      )}
    </div>
  );
}
